import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useEditor, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import CodeBlock from "@tiptap/extension-code-block";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import {
  X, Check, ChevronDown, Pencil, Loader2, Plus,
  HelpCircle, Info, BookOpen, AlertTriangle,
  Bold, Italic, Underline as UnderlineIcon, Code, Heading2, List,
} from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import { listNodePages, updateNodePage, type PageCategory } from "@/services/nodePageService";
import { useNodePageStore, type FocusModePage, type PageCat } from "@/store/useNodePageStore";
import EditorToolbar from "./EditorToolbar";
import { CV } from "@/config/canvasSizing";
import { pageCategoryStyle, THEME } from "@/config/theme";

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORIES: { value: PageCat; label: string; icon: typeof HelpCircle }[] = [
  { value: "question",  label: "Question",  icon: HelpCircle },
  { value: "info",      label: "Info",      icon: Info },
  { value: "deep-dive", label: "Deep Dive", icon: BookOpen },
  { value: "critical",  label: "Critical",  icon: AlertTriangle },
];

function getCatEntry(cat: PageCat) {
  return CATEGORIES.find((c) => c.value === cat) ?? CATEGORIES[1];
}

// ── Inner editor (remounts per pageId via key) ────────────────────────────────

interface InnerProps extends FocusModePage {
  onClose: () => void;
}

function FocusModeEditorInner({
  pageId, nodeId, nodeLabel,
  initialTitle, initialCategory, initialContent,
  initialMode = "edit",
  onClose,
}: InnerProps) {
  const { getToken } = useAuth();
  const upsertPage = useNodePageStore((s) => s.upsertPage);

  // Track whether this session started in view mode so Done knows to return to view vs close
  const startedInViewRef = useRef(initialMode === "view");

  const [mode, setMode]             = useState<"view" | "edit">(initialMode);
  const [title, setTitle]           = useState(initialTitle);
  const [category, setCategory]     = useState<PageCat>(initialCategory);
  const [catOpen, setCatOpen]       = useState(false);
  const [autoSaveStatus, setStatus] = useState<"idle" | "saving" | "saved">("idle");
  // null while fetching, string once ready
  const [loadedContent, setLoadedContent] = useState<string | null>(initialContent);

  // Rich card fields — seeded from the loaded page (async), authored here.
  const [summary, setSummary] = useState("");
  const [code, setCode]       = useState("");
  const [asks, setAsks]       = useState<string[]>([]);

  const inputRef       = useRef<HTMLInputElement>(null);
  const catRef         = useRef<HTMLDivElement>(null);
  const debounceRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleValRef    = useRef(title);
  const categoryValRef = useRef(category);
  const modeRef        = useRef(mode);
  const summaryValRef  = useRef(summary);
  const codeValRef     = useRef(code);
  const asksValRef     = useRef(asks);
  const loadedRef      = useRef(loadedContent);
  useEffect(() => { titleValRef.current    = title;    }, [title]);
  useEffect(() => { categoryValRef.current = category; }, [category]);
  useEffect(() => { modeRef.current        = mode;     }, [mode]);
  useEffect(() => { summaryValRef.current  = summary;  }, [summary]);
  useEffect(() => { codeValRef.current     = code;     }, [code]);
  useEffect(() => { asksValRef.current     = asks;     }, [asks]);
  useEffect(() => { loadedRef.current      = loadedContent; }, [loadedContent]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false, link: false, underline: false }),
      Underline,
      CodeBlock,
      Link.configure({ openOnClick: false }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder: "Start writing…" }),
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color,
    ],
    content: "",
    editable: initialMode === "edit",
    autofocus: initialMode === "edit" ? "end" : false,
  });

  // Load the page: always fetch so the rich fields (summary/code/asks) hydrate.
  // Content still prefers initialContent when provided (avoids blocking the
  // editor on the fetch); we only fall back to the fetched content when it's null.
  useEffect(() => {
    if (initialContent !== null) setLoadedContent(initialContent);
    listNodePages(getToken, nodeId)
      .then((pages) => {
        const p = pages.find((x) => x.id === pageId);
        if (initialContent === null) setLoadedContent(p?.content ?? "");
        if (p) {
          setSummary(p.summary ?? "");
          setCode(p.code ?? "");
          setAsks(p.asks ?? []);
        }
      })
      .catch(() => { if (initialContent === null) setLoadedContent(""); });
  }, [getToken, nodeId, pageId, initialContent]);

  // Set editor content once both editor and content are ready
  useEffect(() => {
    if (!editor || loadedContent === null) return;
    editor.commands.setContent(loadedContent);
  }, [editor, loadedContent]);

  // Sync editor editable state when mode changes
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(mode === "edit");
  }, [editor, mode]);

  // Click outside category dropdown → close it
  useEffect(() => {
    if (!catOpen) return;
    const handler = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [catOpen]);

  // Select title when opening in edit mode
  useEffect(() => {
    if (initialMode !== "edit") return;
    const t = setTimeout(() => inputRef.current?.select(), 300);
    return () => clearTimeout(t);
  }, [initialMode]);

  const autosave = useCallback(async (html: string) => {
    setStatus("saving");
    try {
      await updateNodePage(
        getToken, pageId,
        titleValRef.current, categoryValRef.current as PageCategory, html,
        {
          summary: summaryValRef.current,
          code: codeValRef.current,
          asks: asksValRef.current,
        },
      );
      upsertPage(nodeId, {
        id: pageId, node_id: nodeId, org_id: "", title: titleValRef.current,
        category: categoryValRef.current, content: html,
        summary: summaryValRef.current, code: codeValRef.current,
        asks: asksValRef.current, created_at: "", updated_at: "",
      });
      setStatus("saved");
      setTimeout(() => setStatus("idle"), 2000);
    } catch {
      setStatus("idle");
    }
  }, [getToken, pageId, nodeId, upsertPage]);

  // Autosave on editor content change (edit mode only)
  useEffect(() => {
    if (!editor) return;
    const handler = () => {
      if (modeRef.current !== "edit") return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => autosave(editor.getHTML()), 600);
    };
    editor.on("update", handler);
    return () => {
      editor.off("update", handler);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [editor, autosave]);

  // Autosave on title / category change (edit mode only)
  useEffect(() => {
    if (!editor || modeRef.current !== "edit") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => autosave(editor.getHTML()), 600);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, category]);

  // Autosave on rich-field change (edit mode only). Not gated on `editor` — the
  // rich fields are edited via plain inputs, so they must persist even before the
  // tiptap editor is ready; fall back to the last loaded HTML for the content.
  useEffect(() => {
    if (modeRef.current !== "edit") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => autosave(editor ? editor.getHTML() : loadedRef.current ?? ""),
      600,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, code, asks]);

  const enterEditMode = useCallback(() => {
    setMode("edit");
    if (editor) {
      editor.setEditable(true);
      setTimeout(() => editor.commands.focus("end"), 50);
    }
  }, [editor]);

  // Flush any pending debounced save, then close. Every close affordance (X,
  // backdrop, Escape) routes through this so edits made inside the last 600ms
  // debounce window aren't dropped when the component unmounts — the drawer
  // re-reads on close, so the write must land first.
  const requestClose = useCallback(async () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (modeRef.current === "edit") {
      await autosave(editor ? editor.getHTML() : loadedRef.current ?? "");
    }
    onClose();
  }, [editor, autosave, onClose]);

  const handleDone = useCallback(async () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    await autosave(editor ? editor.getHTML() : loadedRef.current ?? "");
    // Return to view if we started in view mode, otherwise close
    if (startedInViewRef.current) {
      setMode("view");
    } else {
      onClose();
    }
  }, [editor, autosave, onClose]);

  // Escape → flush + close (declared after requestClose so it can reference it)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") void requestClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [requestClose]);

  const entry = getCatEntry(category);
  const isLoading = loadedContent === null;

  return (
    // Backdrop
    <motion.div
      data-testid="focus-mode-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      style={{ willChange: "opacity" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      onClick={() => void requestClose()}
    >
      {/* Card */}
      <motion.div
        className="relative bg-[#161619] border border-white/10 shadow-2xl shadow-black/60 rounded-[18px] flex flex-col overflow-hidden"
        style={{
          width:     CV.focusModal.width,
          maxWidth:  CV.focusModal.maxWidth,
          height:    CV.focusModal.height,
          maxHeight: CV.focusModal.maxHeight,
          willChange: "opacity, transform",
          contain: "layout style paint",
        }}
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => {
          if (catOpen && catRef.current && !catRef.current.contains(e.target as Node)) {
            setCatOpen(false);
          }
        }}
      >
        {/* Top-right actions */}
        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
          {mode === "view" && (
            <button
              data-testid="focus-edit-btn"
              onClick={enterEditMode}
              className="flex items-center gap-1 text-xs text-canvas-muted hover:text-canvas-text px-2.5 py-1.5 rounded-lg hover:bg-white/5 transition-colors"
            >
              <Pencil size={11} />
              Edit
            </button>
          )}
          <button
            data-testid="focus-mode-close"
            onClick={() => void requestClose()}
            aria-label="Close focus mode"
            className="flex items-center justify-center w-7 h-7 rounded-lg border border-white/10 text-canvas-muted hover:text-canvas-text hover:bg-white/5 transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Header: breadcrumb + title + category */}
        <div className="px-12 pt-10 pb-4 shrink-0">
          <p className="text-[10px] font-mono text-canvas-muted uppercase tracking-[0.14em] mb-3">
            {mode === "edit" ? `Edit page · ${nodeLabel}` : nodeLabel}
          </p>

          {mode === "edit" ? (
            <input
              ref={inputRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Page title"
              className="w-full text-3xl font-bold text-canvas-text bg-transparent outline-none placeholder:text-zinc-600 mb-3 pr-24"
              data-testid="focus-title-input"
            />
          ) : (
            <h1 className="text-3xl font-bold text-canvas-text mb-3 pr-24">
              {title || "Untitled"}
            </h1>
          )}

          {mode === "edit" ? (
            <div ref={catRef} className="relative">
              <button
                onClick={() => setCatOpen((o) => !o)}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors ${pageCategoryStyle(category).pill}`}
                data-testid="focus-category-selector"
              >
                <entry.icon size={14} />
                <span>{entry.label}</span>
                <ChevronDown size={9} className="opacity-70" />
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 mt-1 z-50 w-36 bg-canvas-node border border-canvas-node-border rounded-lg shadow-lg py-1">
                  {CATEGORIES.map((cat) => {
                    const CIcon = cat.icon;
                    const cStyle = pageCategoryStyle(cat.value);
                    const isSel = cat.value === category;
                    return (
                      <button
                        key={cat.value}
                        onClick={() => { setCategory(cat.value); setCatOpen(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors hover:bg-white/5 ${
                          isSel ? cStyle.color : "text-canvas-text"
                        }`}
                      >
                        <CIcon size={11} className={cStyle.color} />
                        {cat.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg ${pageCategoryStyle(category).pill}`}>
              <entry.icon size={14} />
              <span>{entry.label}</span>
            </span>
          )}
        </div>

        {/* Toolbar strip — edit mode only */}
        {mode === "edit" && (
          <div data-testid="focus-toolbar-strip" className="px-12 py-2 border-t border-b border-canvas-node-border shrink-0 bg-white/[0.02]">
            {editor && <EditorToolbar editor={editor} variant="dark" />}
          </div>
        )}

        {/* BubbleMenu — edit mode only */}
        {editor && mode === "edit" && (
          <BubbleMenu editor={editor}>
            <div className="flex items-center gap-0.5 bg-zinc-900 border border-white/10 rounded-lg px-1 py-1 shadow-xl">
              {[
                { icon: Bold,          action: () => editor.chain().toggleBold().run(),                active: editor.isActive("bold") },
                { icon: Italic,        action: () => editor.chain().toggleItalic().run(),              active: editor.isActive("italic") },
                { icon: UnderlineIcon, action: () => editor.chain().toggleUnderline().run(),           active: editor.isActive("underline") },
                { icon: Code,          action: () => editor.chain().toggleCode().run(),                active: editor.isActive("code") },
                { icon: Heading2,      action: () => editor.chain().toggleHeading({ level: 2 }).run(), active: editor.isActive("heading", { level: 2 }) },
                { icon: List,          action: () => editor.chain().toggleBulletList().run(),          active: editor.isActive("bulletList") },
              ].map(({ icon: Icon, action, active }, i) => (
                <button
                  key={i}
                  onClick={action}
                  className={`p-1.5 rounded transition-colors ${
                    active ? "bg-white/20 text-white" : "text-zinc-400 hover:text-zinc-100 hover:bg-white/10"
                  }`}
                >
                  <Icon size={13} />
                </button>
              ))}
            </div>
          </BubbleMenu>
        )}

        {/* Scrollable editor */}
        <div className="flex-1 overflow-y-auto px-10 pt-6 pb-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 size={20} className="animate-spin text-zinc-600" />
            </div>
          ) : (
            <>
              <div className="bg-[#0a0a0d] border border-[#1f1f23] rounded-[10px]">
                <EditorContent
                  editor={editor}
                  className="prose prose-invert prose-lg max-w-none text-canvas-text outline-none px-10 pt-4 pb-8"
                />
              </div>

              {/* Rich card fields — authored in edit mode only */}
              {mode === "edit" && (
                <div className="mt-8 space-y-7">
                  {/* Summary */}
                  <section data-testid="focus-summary-section">
                    <label className={`block text-[10px] mb-2 ${THEME.pageCard.label} ${THEME.pageCard.summary}`}>
                      Summary
                    </label>
                    <textarea
                      data-testid="focus-summary-input"
                      value={summary}
                      onChange={(e) => setSummary(e.target.value)}
                      placeholder="One-line summary shown on the page card"
                      rows={2}
                      className="w-full resize-none rounded-[10px] bg-white/[0.02] border border-white/[0.06] px-3 py-2 text-sm text-canvas-text outline-none placeholder:text-zinc-600 focus:border-indigo-400/40 transition-colors"
                    />
                  </section>

                  {/* Snippet / code */}
                  <section data-testid="focus-code-section">
                    <label className={`block text-[10px] mb-2 ${THEME.pageCard.label} ${THEME.pageCard.snippet}`}>
                      Snippet
                    </label>
                    <textarea
                      data-testid="focus-code-input"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Optional code snippet"
                      rows={4}
                      spellCheck={false}
                      className="w-full resize-y rounded-[10px] bg-canvas-bg border border-canvas-node-border px-3 py-2 font-mono text-[13px] leading-relaxed text-emerald-300 outline-none placeholder:text-zinc-600 focus:border-emerald-400/40 transition-colors"
                    />
                  </section>

                  {/* You asked */}
                  <section data-testid="focus-asks-section">
                    <div className="flex items-center justify-between mb-2">
                      <label className={`text-[10px] ${THEME.pageCard.label} ${THEME.pageCard.ask}`}>
                        You asked
                      </label>
                      <button
                        type="button"
                        data-testid="focus-ask-add"
                        onClick={() => setAsks((a) => [...a, ""])}
                        className="flex items-center gap-1 text-[11px] text-violet-400 hover:text-violet-300 transition-colors"
                      >
                        <Plus size={11} />
                        Add
                      </button>
                    </div>
                    {asks.length === 0 ? (
                      <p className="text-xs text-zinc-600">No questions yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {asks.map((ask, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <input
                              data-testid={`focus-ask-input-${i}`}
                              value={ask}
                              onChange={(e) =>
                                setAsks((a) => a.map((v, j) => (j === i ? e.target.value : v)))
                              }
                              placeholder="A question the learner raised"
                              className={`flex-1 rounded-lg px-3 py-1.5 text-sm outline-none placeholder:text-zinc-600 focus:bg-violet-400/[0.12] transition-colors ${THEME.pageCard.askItem}`}
                            />
                            <button
                              type="button"
                              data-testid={`focus-ask-remove-${i}`}
                              onClick={() => setAsks((a) => a.filter((_, j) => j !== i))}
                              aria-label="Remove question"
                              className="flex items-center justify-center w-7 h-7 shrink-0 rounded-lg text-canvas-muted hover:text-canvas-text hover:bg-white/5 transition-colors"
                            >
                              <X size={13} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer — edit mode only */}
        {mode === "edit" && (
          <div className="px-12 py-4 border-t border-canvas-node-border shrink-0 flex items-center justify-between bg-white/[0.02]">
            <span
              data-testid="focus-autosave-indicator"
              className={`text-[11px] font-mono text-canvas-muted transition-opacity duration-300 ${
                autoSaveStatus !== "idle" ? "opacity-100" : "opacity-0"
              }`}
            >
              {autoSaveStatus === "saving" ? "Saving…" : "✓ Saved"}
            </span>
            <button
              onClick={handleDone}
              data-testid="focus-done-btn"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-[9px] bg-gradient-to-br from-indigo-500 to-indigo-400 text-white text-sm font-semibold hover:from-indigo-400 hover:to-indigo-300 transition-colors shadow-[0_6px_18px_-6px_rgba(99,102,241,0.7)]"
            >
              <Check size={13} />
              Done
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// ── Shell — reads store, mounts inner on focusMode change ─────────────────────

export default function FocusModeEditor() {
  const focusMode      = useNodePageStore((s) => s.focusMode);
  const closeFocusMode = useNodePageStore((s) => s.closeFocusMode);

  return (
    <AnimatePresence>
      {focusMode && (
        <FocusModeEditorInner
          key={focusMode.pageId}
          {...focusMode}
          onClose={closeFocusMode}
        />
      )}
    </AnimatePresence>
  );
}
